import { IAccordionRow, ICON_NAMES, IPlanCard, TPlanType } from '@belong/types';
import { FLAIRS, FONT_COLOURS } from '@belong/themes';

import { TopLevelBlock } from '@contentful/rich-text-types';
import { RichText } from '../../styles/Typography';
import { ActionRowList } from '../../lists';
import { ACCORDION_ROW_STATE, AccordionRow, ReadOnlyRow } from '../../actions';
import { IMultipleAccordionPlanCardProps, PlanSelectionStatus } from './types';

export interface IStatusDetails
  extends Pick<IPlanCard, 'cardStripColour' | 'differentiatorIcon' | 'differentiatorLabel'> {
  selectionStatus?: PlanSelectionStatus;
}
export const getStatusDetails = ({
  current,
  isPlanPermitted,
  selected
}: {
  current?: boolean;
  isPlanPermitted?: boolean;
  selected?: boolean;
}): IStatusDetails => {
  if (current) {
    return {
      selectionStatus: 'current',
      cardStripColour: FLAIRS.GREEN,
      differentiatorIcon: { name: ICON_NAMES.TickCircle },
      differentiatorLabel: 'Current plan'
    };
  }

  if (selected) {
    return {
      selectionStatus: 'selected',
      cardStripColour: FLAIRS.GREEN
    };
  }

  if (!isPlanPermitted) {
    return {
      selectionStatus: 'unavailable',
      cardStripColour: FLAIRS.UNAVAILABLE
    };
  }

  return {};
};

interface IAccordionRowListItems
  extends Pick<IMultipleAccordionPlanCardProps, 'accordionRefs' | 'toggleAccordions' | 'accordionRowContentRefs'> {
  accordionRowList: IAccordionRow[];
  columnIndex: number;
  startIndex?: number;
}
export const getAccordionRowListItems = ({
  accordionRefs,
  accordionRowList,
  columnIndex,
  startIndex = 0,
  toggleAccordions,
  accordionRowContentRefs
}: IAccordionRowListItems) => {
  return accordionRowList.map(
    ({ identifier, label, headingLevel, body, available, icon, listItems, hasBackground }, accordionIndex) => {
      const rowIndex = accordionIndex + startIndex;
      return (
        <AccordionRow
          key={identifier}
          label={label}
          headingLevel={headingLevel}
          variant="slim"
          available={available}
          icon={icon}
          hasBackground={hasBackground}
          initialState={ACCORDION_ROW_STATE.closed}
          ref={ref => {
            if (accordionRefs?.current && ref) {
              const updatedAccordionRefs = [...accordionRefs.current];
              updatedAccordionRefs[rowIndex] = updatedAccordionRefs[rowIndex] || [];
              updatedAccordionRefs[rowIndex][columnIndex] = ref;
              // eslint-disable-next-line no-param-reassign
              accordionRefs.current = updatedAccordionRefs;
            }
          }}
          onClick={() => {
            toggleAccordions?.(rowIndex);
          }}
          unmountCollapsedContent={false} // allow crawlers to read the content and the content to be measured
        >
          <>
            <ActionRowList>
              {listItems &&
                listItems.map(
                  (
                    {
                      identifier: itemId,
                      icon: itemIcon,
                      heading: itemHeading,
                      title: itemTitle,
                      description,
                      available: itemAvailable
                    },
                    listItemIndex
                  ) => (
                    <ReadOnlyRow
                      key={itemId}
                      heading={itemHeading}
                      title={itemTitle}
                      description={
                        description && (
                          <RichText
                            {...description}
                            hasColor={itemAvailable ? FONT_COLOURS.BASE : FONT_COLOURS.FINE_PRINT}
                          />
                        )
                      }
                      icon={itemIcon}
                      isTopAligned
                      isActive={itemAvailable}
                      ref={ref => {
                        if (accordionRowContentRefs?.current && ref) {
                          const updatedAccordionRowContentRefs = [...accordionRowContentRefs.current];

                          updatedAccordionRowContentRefs[rowIndex] = updatedAccordionRowContentRefs[rowIndex] || [];
                          updatedAccordionRowContentRefs[rowIndex][listItemIndex] =
                            updatedAccordionRowContentRefs[rowIndex][listItemIndex] || [];
                          updatedAccordionRowContentRefs[rowIndex][listItemIndex][columnIndex] = ref;
                          // eslint-disable-next-line no-param-reassign
                          accordionRowContentRefs.current = updatedAccordionRowContentRefs;
                        }
                      }}
                    />
                  )
                )}
            </ActionRowList>
            {body && <RichText {...body} />}
          </>
        </AccordionRow>
      );
    }
  );
};

const mobilePlanTypes: TPlanType[] = ['Mobile', 'Data'];
export const isMobilePlan = (planType: TPlanType) => mobilePlanTypes.includes(planType);

export const isNbnPlan = (planType: TPlanType) => planType === 'nbn';

interface IIconFields {
  identifier: string;
  icon: string;
  colour: string;
  size: string;
}

export const findElementSystemIconFields = (data: TopLevelBlock[]): IIconFields[] => {
  const result: IIconFields[] = [];

  const traverseNodes = nodes => {
    nodes.forEach((node: TopLevelBlock) => {
      if (
        (node.nodeType as string) === 'embedded-entry-inline' &&
        node.data.target?.sys.contentType.sys.id === 'elementSystemIcon'
      ) {
        result.push(node.data.target.fields as IIconFields);
      }
      if (node.content) {
        traverseNodes(node.content as TopLevelBlock[]);
      }
    });
  };

  traverseNodes(data);
  return result;
};
